/**
 * Work in progress a page keeps for its browser tab, so that visiting another screen does
 * not throw it away (TRKC-110: a DAT paste under review vanished the moment the dispatcher
 * looked at a broker). It lives in `sessionStorage` — this tab only, gone when the tab
 * closes — under the user who made it, and signing out forgets it all.
 *
 * <p>
 * Storage can be refused (a private window, blocked site data) or full: every read and
 * write is guarded, and a page that cannot keep its draft simply works as it did before.
 * </p>
 */

const PREFIX = 'tc.draft.'

interface Kept<T> {
  user: string
  draft: T
}

export function keepDraft<T>(name: string, user: string, draft: T): void {
  try {
    sessionStorage.setItem(PREFIX + name, JSON.stringify({ user, draft } satisfies Kept<T>))
  } catch {
    // Not kept: the page still works, it just starts empty after a visit elsewhere. An older
    // draft left in place would come back as if it were this one — corrections undone, a load
    // created since said as not — so it goes (Copilot on #58).
    forgetDraft(name)
  }
}

/** The draft this user kept here, or null — another user's is never handed over. */
export function keptDraft<T>(name: string, user: string): T | null {
  try {
    const raw = sessionStorage.getItem(PREFIX + name)
    if (raw === null) {
      return null
    }

    const kept = JSON.parse(raw) as Partial<Kept<T>>
    return kept.user === user && kept.draft !== undefined ? kept.draft : null
  } catch {
    return null
  }
}

export function forgetDraft(name: string): void {
  try {
    sessionStorage.removeItem(PREFIX + name)
  } catch {
    // Nothing to forget, or nothing we may touch.
  }
}

/** Every page's draft in this tab — what signing out leaves behind for the next person. */
export function forgetAllDrafts(): void {
  try {
    Object.keys(sessionStorage)
      .filter((key) => key.startsWith(PREFIX))
      .forEach((key) => sessionStorage.removeItem(key))
  } catch {
    // As above.
  }
}
